// React and core libraries
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// MUI components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

// Components
import Spinner from "../spinner/Spinner";
import PublishStatePDFFormDialog from "./PublishStatePDFFormDialog";

// Services
import { logout } from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// Config
import { appConfig } from "../../config/appConfig";

// Assets
import visiable from "../../assets/visiable.svg";
import plusaccor from "../../assets/accordian/plus-accordian.svg";
import minusaccor from "../../assets/accordian/minus-accordian.svg";

// Styles
import "./PublishStateData.scss";

interface State {
  id: number;
  name: string;
}
type AdminEntry = {
  [x: string]: any;
  id: number;
  state: string;
  stateName: string;
  pdFname: string;
  pdfUrls: string | string[];
  isActive: boolean;
  status: string;
};
interface count {
  setStatePDFCount: any;
  visible: boolean;
}
const PublishStatePDF: React.FC<count> = ({ setStatePDFCount, visible }) => {
  // useState
  const [dataList, setDataList] = useState<AdminEntry[]>([]);
  const [filteredDataList, setFilteredDataList] = useState<AdminEntry[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [expandedState, setExpandedState] = useState<string | false>(false);
  const [formData, setFormData] = useState<AdminEntry>({
    id: 0,
    state: "",
    stateName: "",
    pdFname: "",
    pdfUrls: [],
    isActive: false,
    status: "",
  });
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;
  const [loading, setLoading] = useState(false);
  const [statefilter, setstatefilter] = useState("");

  // useNavigate
  const navigate = useNavigate();

  // Grouping data by state
  const groupedByState = filteredDataList.reduce((acc, item) => {
    if (!acc[item.stateName]) {
      acc[item.stateName] = [];
    }
    acc[item.stateName].push(item);
    return acc;
  }, {} as { [key: string]: typeof dataList });

  // useEffect: filter by state

  useEffect(() => {
    const filtered = statefilter
      ? dataList.filter((item) => item.state === statefilter)
      : dataList;

    setFilteredDataList(filtered);
  }, [statefilter, dataList]);

  useEffect(() => {
    const filteredData = dataList.filter((item) => item.status === "Pending");

    setFilteredDataList(filteredData);
    setStatePDFCount(filteredData.length);
  }, [dataList]);

  useEffect(() => {
    const savedData = localStorage.getItem("adminDataList");
    if (savedData) {
      const parsedData: AdminEntry[] = JSON.parse(savedData);
      setDataList(parsedData);
      setFilteredDataList(parsedData);

      if (!savedData || parsedData.length === 0) {
        setEditingIndex(null);
      }
    }
  }, []);

  //Fetch Categories and states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(
          `${appConfig.serviceUrls.getAdminEntries}/states`
        );
        if (!response.ok) throw new Error("Failed to fetch states");
        const data = await response.json();
        setStates(data);
      } catch (error) {
        console.error("Error fetching states", error);
      }
    };
    //call the fetch functions
    fetchStates();
  }, []);

  useEffect(() => {
    if (editingIndex !== null) {
      const selectedEntry = dataList.find((item) => item.id === editingIndex);
      if (selectedEntry) {
        const pdfUrls =
          typeof selectedEntry.pdfUrls === "string"
            ? selectedEntry.pdfUrls.split(",")
            : Array.isArray(selectedEntry.pdfUrls)
            ? selectedEntry.pdfUrls
            : [];
        setFormData({
          ...selectedEntry,
          pdfUrls,
        });
      }
    }
  }, [editingIndex, dataList]);

  useEffect(() => {
    fetchData();
  }, []);
  const resetForm = () => {
    //Reset Form
    setFormData({
      id: dataList.length + 1, //Increment ID
      state: "",
      pdFname: "",
      pdfUrls: [],
      isActive: false,
      status: "",
      stateName: "",
    });
    setEditingIndex(null);
  };

  // handle approve
  const handleApprove = async (id: number) => {
    if (!window.confirm("Are you sure want to approve & publish this entry?"))
      return;
    await handlePublish(id, "Published", "3");
  };
  // handle reject
  const handleReject = async (id: number) => {
    if (!window.confirm("Are you sure want to reject this entry?")) return;
    await handlePublish(id, "Rejected", "2");
  };

  //handle Publish

  const handlePublish = async (id: number, stat: string, statcode: string) => {
    // if (!window.confirm("Are you sure you want to publish this entry?")) return;

    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }
    const selectedEntry = dataList.find((item) => item.id === id);

    if (!selectedEntry) {
      console.error("No entry found at index:", id);
      return;
    }
    setLoading(true);
    const formDataToSend = new FormData();
    formDataToSend.append("id", selectedEntry.id.toString());
    // formDataToSend.append("status", "Published");
    formDataToSend.append("state", selectedEntry.state);
    formDataToSend.append("pdFname", selectedEntry.pdFname);

    formDataToSend.append("isActive", selectedEntry.isActive.toString());
    formDataToSend.append("uploadBy", selectedEntry.uploadBy);
    // formDataToSend.append("statusCode", "3");

    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("approveBy", username);
    formDataToSend.append("publishBy", username);
    formDataToSend.append("status", stat); // override
    formDataToSend.append("statusCode", statcode);
    // formDataToSend.append("pdfUrls", selectedEntry.pdfUrls.toString());
    formDataToSend.append("pdfUrls", selectedEntry.pdfUrls?.toString() ?? "");

    // formDataToSend.append("pdfUrls", selectedEntry.pdfUrls.toString());

    const url = `${appConfig.serviceUrls.saveStatePDFEntries}/${selectedEntry.id}`;
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: "PUT",
        body: formDataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }
      if (!response.ok) throw new Error("Failed to publish entry");

      alert(`Data ${stat} successfully`);
      fetchData();
      resetForm();
      setEditingIndex(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error publishing entry:", error);
      alert("Network Error.");
    } finally {
      setLoading(false);
    }
  };
  const fetchData = async () => {
    setLoading(true);
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(appConfig.serviceUrls.saveStatePDFEntries, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data: AdminEntry[] = await response.json();

      setDataList(data || []);
      setFilteredDataList(data || []);
      //setDataList(formattedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      //setDataList([]);
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;
  return (
    <>
      {loading ? (
        <>
          {" "}
          <Spinner></Spinner>
        </>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ padding: 0, width: "100%", maxHeight: "80vh" }}
        >
          {Object.keys(groupedByState).length > 0 ? (
            <>
              {(() => {
                const sortedStateKeys = Object.keys(groupedByState).sort(
                  (a, b) => a.localeCompare(b)
                );

                // Paginate the sorted state keys
                const paginatedStates = sortedStateKeys.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                );

                return (
                  <>
                    {paginatedStates.map((state) => {
                      const entries = groupedByState[state];

                      return (
                        <Accordion
                          key={state}
                          expanded={expandedState === state}
                          onChange={() =>
                            setExpandedState(
                              expandedState === state ? false : state
                            )
                          }
                          sx={{ marginBottom: 0 }}
                        >
                          <AccordionSummary
                            expandIcon={
                              <img
                                src={
                                  expandedState === state
                                    ? minusaccor
                                    : plusaccor
                                }
                                alt="toggle"
                                style={{ width: 20, height: 20 }}
                              />
                            }
                          >
                            <Typography
                              sx={{ fontWeight: "bold", fontSize: "15px" }}
                            >
                              {state}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Table size="medium">
                              <TableHead>
                                <TableRow className="custom-table-head-row">
                                  <TableCell>
                                    <b>Request ID</b>
                                  </TableCell>
                                  <TableCell>
                                    <b>Name</b>
                                  </TableCell>
                                  <TableCell>
                                    <b>PDF File</b>
                                  </TableCell>
                                  <TableCell>
                                    <b>Active / Disable</b>
                                  </TableCell>
                                  <TableCell>
                                    <b>Status</b>
                                  </TableCell>
                                  <TableCell>
                                    <b>Actions</b>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {entries.map((item) => (
                                  <TableRow key={item.id}>
                                    <TableCell className="nowrap">
                                      MAP-{item.id || "\u00A0"}
                                    </TableCell>
                                    <TableCell>
                                      {item.pdFname || "\u00A0"}
                                    </TableCell>

                                    <TableCell>
                                      {typeof item.pdfUrls === "string" &&
                                      item.pdfUrls.trim() !== "" ? (
                                        <Box className="description-wraper">
                                          <Typography className="description-txt">
                                            View PDF
                                          </Typography>

                                          <div>
                                            <a
                                              href={item.pdfUrls.trim()}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              <IconButton
                                                color="primary"
                                                size="small"
                                              >
                                                <img
                                                  src={visiable}
                                                  alt="visible"
                                                  className="img-style"
                                                />
                                              </IconButton>
                                            </a>
                                          </div>
                                        </Box>
                                      ) : (
                                        <Typography>No PDF</Typography>
                                      )}
                                    </TableCell>

                                    <TableCell className="nowrap">
                                      <Box className="status-wrapper">
                                        <Box
                                          className={`status-toggle1 ${
                                            item.isActive ? "active" : "Disable"
                                          }`}
                                        >
                                          <Box className="status-circle" />
                                        </Box>

                                        <Typography
                                          className="active-txt"
                                          component="span"
                                        >
                                          {item.isActive ? "Active" : "Disable"}
                                        </Typography>
                                      </Box>
                                    </TableCell>

                                    {item.status === "Approved" ? (
                                      <TableCell className="approved-text">
                                        {item.status}
                                      </TableCell>
                                    ) : item.status === "Published" ? (
                                      <TableCell className="publish-txt">
                                        {item.status}
                                      </TableCell>
                                    ) : item.status === "Pending" ? (
                                      <TableCell className="pending-txt">
                                        {item.status}
                                      </TableCell>
                                    ) : (
                                      <TableCell>{item.status}</TableCell>
                                    )}

                                    <TableCell
                                      align="left"
                                      className="nowrap display-btn"
                                    >
                                      <IconButton
                                        className="reject-box"
                                        size="small"
                                        onClick={() => {
                                          handleReject(item.id);
                                        }}
                                      >
                                        <Typography className="box-text">
                                          Reject
                                        </Typography>
                                      </IconButton>

                                      <IconButton
                                        className="approve-box"
                                        size="small"
                                        onClick={() => {
                                          handleApprove(item.id);
                                        }}
                                      >
                                        <Typography className="box-text">
                                          Approve & Publish
                                        </Typography>
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}

                    {/* Pagination at the bottom */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "1rem",
                        // position: "absolute",
                        bottom: 0,
                        // backgroundColor: "#fff",
                        zIndex: 1,
                        // borderTop: "1px solid #ccc",
                        overflowX: "hidden",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#000",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "normal",
                        }}
                      >
                        Showing {page * rowsPerPage + 1} to{" "}
                        {Math.min(
                          Object.keys(groupedByState).length,
                          page * rowsPerPage + rowsPerPage
                        )}{" "}
                        of {Object.keys(groupedByState).length} entries
                      </Typography>

                      <Pagination
                        count={Math.ceil(
                          Object.keys(groupedByState).length / rowsPerPage
                        )}
                        page={page + 1}
                        onChange={(e, value) => setPage(value - 1)}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                      />
                    </Box>
                  </>
                );
              })()}
            </>
          ) : (
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1rem",
              }}
            >
              <Typography align="center">No records found.</Typography>
            </Box>
          )}
        </TableContainer>
      )}

      <PublishStatePDFFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        fetchData={fetchData}
        resetForm={resetForm}
        formData={formData}
        setFormData={setFormData}
        editingIndex={editingIndex}
        setShowForm={setShowForm}
        setEditingIndex={setEditingIndex}
        states={states}
        dataList={dataList}
      />
    </>
  );
};

export default PublishStatePDF;
